import React, { useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import { useAuth } from "../../app/providers/AuthProvider";

const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DIGITS_ONLY_REGEX = /^\d+$/;
const TEMPLATE_NAME_REGEX = /^[a-z0-9_]+$/;
const MIN_VALIDITY_DAYS = 1;
const MAX_VALIDITY_DAYS = 730;
const MINUTES_PER_DAY = 24 * 60;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const defaultForm = {
  businessId: "",
  senderPhoneNumberId: "",
  templateName: "",
  templateLanguage: "en_US",
};

function parseCustomValidityDays(rawValue) {
  const raw = String(rawValue ?? "");
  if (!DIGITS_ONLY_REGEX.test(raw)) return null;

  const days = Number(raw);
  if (
    !Number.isSafeInteger(days) ||
    days < MIN_VALIDITY_DAYS ||
    days > MAX_VALIDITY_DAYS
  ) {
    return null;
  }

  return days;
}

function formatLocalDateTime(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return null;

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
}

function readErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function validateForm(form, validityOption, customValidityDays) {
  const errors = {};

  const businessId = String(form.businessId || "").trim();
  if (!businessId) {
    errors.businessId = "BusinessId is required.";
  } else if (!GUID_REGEX.test(businessId)) {
    errors.businessId = "BusinessId must be a valid GUID.";
  }

  const senderPhoneNumberId = String(form.senderPhoneNumberId || "").trim();
  if (!senderPhoneNumberId) {
    errors.senderPhoneNumberId = "SenderPhoneNumberId is required.";
  } else if (!DIGITS_ONLY_REGEX.test(senderPhoneNumberId)) {
    errors.senderPhoneNumberId = "SenderPhoneNumberId must contain digits only.";
  }

  const templateName = String(form.templateName || "").trim();
  if (!templateName) {
    errors.templateName = "TemplateName is required.";
  } else if (!TEMPLATE_NAME_REGEX.test(templateName)) {
    errors.templateName = "Use lowercase letters, digits, and underscore only.";
  }

  if (
    validityOption === "custom" &&
    parseCustomValidityDays(customValidityDays) === null
  ) {
    errors.customValidityDays = `Validity in days must be a whole number between ${MIN_VALIDITY_DAYS} and ${MAX_VALIDITY_DAYS}.`;
  }

  return errors;
}

export default function SignupOtpConfig() {
  const { role } = useAuth();
  const safeRole = String(role || "").toLowerCase();
  const isAllowedRole = safeRole === "superadmin" || safeRole === "admin";

  const [form, setForm] = useState(defaultForm);
  const [validityOption, setValidityOption] = useState("365");
  const [customValidityDays, setCustomValidityDays] = useState("");
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [result, setResult] = useState(null);
  const submissionLockRef = useRef(false);

  const selectedDays = useMemo(
    () =>
      validityOption === "custom"
        ? parseCustomValidityDays(customValidityDays)
        : Number(validityOption),
    [customValidityDays, validityOption]
  );
  const errors = useMemo(
    () => validateForm(form, validityOption, customValidityDays),
    [customValidityDays, form, validityOption]
  );
  const isFormValid = Object.keys(errors).length === 0;
  const expiryPreview = useMemo(() => {
    if (selectedDays === null || !Number.isFinite(selectedDays)) return null;
    return formatLocalDateTime(new Date(Date.now() + selectedDays * MILLISECONDS_PER_DAY));
  }, [selectedDays]);

  if (!isAllowedRole) {
    return <Navigate to="/no-access" replace />;
  }

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const showError = field => (submitted || touched[field]) && errors[field];

  const handleGenerateToken = async e => {
    e.preventDefault();
    if (submissionLockRef.current) return;

    setSubmitted(true);
    setApiError("");

    if (!isFormValid || selectedDays === null) {
      return;
    }

    const submittedDays = selectedDays;

    const payload = {
      businessId: String(form.businessId || "").trim(),
      senderPhoneNumberId: String(form.senderPhoneNumberId || "").trim(),
      templateName: String(form.templateName || "").trim(),
      templateLanguage: form.templateLanguage || "en_US",
      expiresInMinutes: submittedDays * MINUTES_PER_DAY,
    };

    submissionLockRef.current = true;
    try {
      setLoading(true);
      setResult(null);

      const response = await axiosClient.post(
        "/auth/signup-phone-otp/config-token",
        payload
      );
      const envelope = response?.data ?? {};
      const responseBody = envelope?.payload ?? envelope?.data ?? envelope;
      const configToken = String(responseBody?.configToken || "").trim();
      const expiresAtUtc = responseBody?.expiresAtUtc || null;
      const successfulResponseTime = new Date();

      if (!configToken) {
        throw new Error("Config token was not returned by server.");
      }

      setResult({
        configToken,
        expiresAtUtc,
        submittedDays,
        successfulResponseTime: successfulResponseTime.toISOString(),
      });
      toast.success("Config token generated successfully.");
    } catch (error) {
      const msg = readErrorMessage(error, "Failed to generate config token.");
      setApiError(msg);
      toast.error(msg);
    } finally {
      submissionLockRef.current = false;
      setLoading(false);
    }
  };

  const copyText = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch {
      toast.error("Unable to copy to clipboard.");
    }
  };

  const tokenValue = result?.configToken || "";
  const envSnippet = tokenValue
    ? `REACT_APP_SIGNUP_OTP_CONFIG_TOKEN=${tokenValue}`
    : "";
  const generatedAtDisplay = result
    ? formatLocalDateTime(result.successfulResponseTime)
    : null;
  const authoritativeExpiry = result?.expiresAtUtc
    ? new Date(result.expiresAtUtc)
    : null;
  const hasValidAuthoritativeExpiry =
    authoritativeExpiry && Number.isFinite(authoritativeExpiry.getTime());
  const fallbackExpiry = result
    ? new Date(
        new Date(result.successfulResponseTime).getTime() +
          result.submittedDays * MILLISECONDS_PER_DAY
      )
    : null;
  const expectedExpiryDisplay = result
    ? formatLocalDateTime(
        hasValidAuthoritativeExpiry ? authoritativeExpiry : fallbackExpiry
      )
    : null;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <h1 className="text-xl font-semibold text-slate-900">Signup OTP Config</h1>
        <p className="text-sm text-slate-600 mt-1">
          Generate a signed config token for anonymous signup OTP requests.
        </p>

        <form
          aria-label="Signup OTP config token"
          onSubmit={handleGenerateToken}
          className="mt-6 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="business-id"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                BusinessId
              </label>
              <input
                id="business-id"
                type="text"
                value={form.businessId}
                onChange={e => setField("businessId", e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, businessId: true }))}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 ${
                  showError("businessId")
                    ? "border-red-400"
                    : "border-slate-300 focus:border-emerald-500"
                }`}
              />
              {showError("businessId") ? (
                <p className="text-xs text-red-600 mt-1">{errors.businessId}</p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="sender-phone-number-id"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                SenderPhoneNumberId
              </label>
              <input
                id="sender-phone-number-id"
                type="text"
                value={form.senderPhoneNumberId}
                onChange={e => setField("senderPhoneNumberId", e.target.value)}
                onBlur={() =>
                  setTouched(prev => ({ ...prev, senderPhoneNumberId: true }))
                }
                placeholder="123456789012345"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 ${
                  showError("senderPhoneNumberId")
                    ? "border-red-400"
                    : "border-slate-300 focus:border-emerald-500"
                }`}
              />
              {showError("senderPhoneNumberId") ? (
                <p className="text-xs text-red-600 mt-1">
                  {errors.senderPhoneNumberId}
                </p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="template-name"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                TemplateName
              </label>
              <input
                id="template-name"
                type="text"
                value={form.templateName}
                onChange={e => setField("templateName", e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, templateName: true }))}
                placeholder="signup_otp_auth"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 ${
                  showError("templateName")
                    ? "border-red-400"
                    : "border-slate-300 focus:border-emerald-500"
                }`}
              />
              {showError("templateName") ? (
                <p className="text-xs text-red-600 mt-1">{errors.templateName}</p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="template-language"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                TemplateLanguage
              </label>
              <select
                id="template-language"
                value={form.templateLanguage}
                onChange={e => setField("templateLanguage", e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500"
              >
                <option value="en_US">en_US</option>
                <option value="hi">hi</option>
                <option value="en">en</option>
              </select>
            </div>

            <div className="space-y-3">
              <div>
                <label
                  htmlFor="token-validity"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Token Validity
                </label>
                <select
                  id="token-validity"
                  value={validityOption}
                  onChange={e => setValidityOption(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500"
                >
                  <option value="180">6 months (180 days)</option>
                  <option value="365">1 year (365 days)</option>
                  <option value="custom">Custom duration</option>
                </select>
              </div>

              {validityOption === "custom" ? (
                <div>
                  <label
                    htmlFor="custom-validity-days"
                    className="block text-sm font-medium text-slate-700 mb-1"
                  >
                    Validity in days
                  </label>
                  <input
                    id="custom-validity-days"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={customValidityDays}
                    onChange={e => setCustomValidityDays(e.target.value)}
                    onBlur={() =>
                      setTouched(prev => ({
                        ...prev,
                        customValidityDays: true,
                      }))
                    }
                    aria-invalid={Boolean(showError("customValidityDays"))}
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 ${
                      showError("customValidityDays")
                        ? "border-red-400"
                        : "border-slate-300 focus:border-emerald-500"
                    }`}
                  />
                  {showError("customValidityDays") ? (
                    <p className="text-xs text-red-600 mt-1">
                      {errors.customValidityDays}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <p className="text-sm text-slate-600" aria-live="polite">
                {expiryPreview
                  ? `Expected token expiry: ${expiryPreview}`
                  : "Enter a valid duration to calculate the expiry date."}
              </p>
            </div>
          </div>

          {apiError ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {apiError}
            </div>
          ) : null}

          <div className="pt-2">
            <button
              type="submit"
              disabled={!isFormValid || loading}
              className="inline-flex items-center justify-center rounded-md bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Generating..." : "Generate Token"}
            </button>
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-900">
            <span className="inline-flex items-center rounded-full bg-amber-100 border border-amber-200 px-2 py-0.5 font-semibold mr-2">
              Note
            </span>
            Required data to send signup OTP safely: BusinessId, SenderPhoneNumberId
            (Meta phone_number_id), TemplateName, TemplateLanguage, and
            token validity. This config token exists so anonymous users cannot
            pass arbitrary business/sender/template values.
          </div>
        </form>
      </div>

      {result ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Generated Token</h2>
            <p className="text-sm text-slate-600 mt-1">
              Use this token in signup OTP send/verify API calls.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Config Token
            </label>
            <textarea
              readOnly
              value={tokenValue}
              rows={4}
              className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-800"
            />
            <div className="mt-2">
              <button
                type="button"
                onClick={() => copyText(tokenValue, "Token copied.")}
                className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Copy Token
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-slate-700">
            <div>
              <span className="font-medium">Selected validity: </span>
              <span>{result.submittedDays} days</span>
            </div>
            <div>
              <span className="font-medium">Token generated: </span>
              <span>{generatedAtDisplay || "N/A"}</span>
            </div>
            <div>
              <span className="font-medium">Expected expiry: </span>
              <span>{expectedExpiryDisplay || "N/A"}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Helper Snippet
            </label>
            <textarea
              readOnly
              value={envSnippet}
              rows={3}
              className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-800"
            />
            <div className="mt-2">
              <button
                type="button"
                onClick={() => copyText(envSnippet, "Snippet copied.")}
                className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Copy Snippet
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
